import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Plus, Trash2, Copy, Edit } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';

const ChartOfAccountsForm = ({ editingItem, onSubmit, onCancel }) => {
  const [formData, setFormData] = useState({
    coa_id: '',
    name: '',
    description: '',
    coa_type: 'operational',
    currency: '',
    accounts: []
  });

  const [showGLAccountDialog, setShowGLAccountDialog] = useState(false);
  const [editingGLAccount, setEditingGLAccount] = useState(null);
  const [glAccountForm, setGLAccountForm] = useState({
    account_number: '',
    account_name: '',
    short_text: '',
    account_group: '',
    account_type: 'asset',
    reconciliation_acct_flag: false,
    allow_postings: true,
    blocked_flag: false,
    group_account_number: ''
  });

  const coaTypes = [
    { value: 'operational', label: 'Operational' },
    { value: 'group', label: 'Group' },
    { value: 'country-specific', label: 'Country-Specific' }
  ];

  const accountTypes = [
    { value: 'asset', label: 'Asset' },
    { value: 'liability', label: 'Liability' },
    { value: 'equity', label: 'Equity' },
    { value: 'revenue', label: 'Revenue' },
    { value: 'expense', label: 'Expense' },
    { value: 'balance', label: 'Balance Sheet' },
    { value: 'profit_loss', label: 'Profit & Loss' }
  ];

  const accountGroups = [
    'ASSET', 'LIAB', 'EQUITY', 'REV', 'EXP', 'CASH', 'RECV', 'PAYB', 'INVEN'
  ];

  useEffect(() => {
    if (editingItem) {
      setFormData(editingItem);
    } else {
      // Apply default template
      applyTemplate('STANDARD');
    }
  // eslint-disable-next-line
  }, [editingItem?.coa_id]);

  const applyTemplate = (templateName) => {
    let template = {};
    
    if (templateName === 'STANDARD') {
      // Generate unique COA ID suggestion
      const coaIdSuggestion = `COA${Math.floor(Math.random() * 100).toString().padStart(2, '0')}`;
      
      template = {
        coa_id: coaIdSuggestion,
        name: 'Standard Chart of Accounts',
        description: 'Standard operational chart with basic account structure',
        coa_type: 'operational',
        currency: 'USD',
        accounts: [
          {
            account_number: '100000',
            account_name: 'Cash and Cash Equivalents',
            short_text: 'Cash',
            account_group: 'CASH',
            account_type: 'asset',
            reconciliation_acct_flag: false,
            allow_postings: true,
            blocked_flag: false,
            group_account_number: ''
          },
          {
            account_number: '110000',
            account_name: 'Accounts Receivable',
            short_text: 'A/R',
            account_group: 'RECV',
            account_type: 'asset',
            reconciliation_acct_flag: true,
            allow_postings: false,
            blocked_flag: false,
            group_account_number: ''
          },
          {
            account_number: '200000',
            account_name: 'Accounts Payable',
            short_text: 'A/P',
            account_group: 'PAYB',
            account_type: 'liability',
            reconciliation_acct_flag: true,
            allow_postings: false,
            blocked_flag: false,
            group_account_number: ''
          },
          {
            account_number: '400000',
            account_name: 'Sales Revenue',
            short_text: 'Sales',
            account_group: 'REV',
            account_type: 'revenue',
            reconciliation_acct_flag: false,
            allow_postings: true,
            blocked_flag: false,
            group_account_number: ''
          },
          {
            account_number: '500000',
            account_name: 'Cost of Goods Sold',
            short_text: 'COGS',
            account_group: 'EXP',
            account_type: 'expense',
            reconciliation_acct_flag: false,
            allow_postings: true,
            blocked_flag: false,
            group_account_number: ''
          }
        ]
      };
    } else if (templateName === 'GROUP') {
      // Group COA template with different account numbers
      const coaIdSuggestion = `GRP${Math.floor(Math.random() * 100).toString().padStart(2, '0')}`;
      
      template = {
        coa_id: coaIdSuggestion,
        name: 'Group Consolidation Chart',
        description: 'Group COA for consolidation reporting',
        coa_type: 'group',
        currency: '',
        accounts: [
          {
            account_number: '1000',
            account_name: 'Total Assets',
            short_text: 'Assets',
            account_group: 'ASSET',
            account_type: 'asset',
            reconciliation_acct_flag: false,
            allow_postings: false,
            blocked_flag: false,
            group_account_number: ''
          },
          {
            account_number: '2000',
            account_name: 'Total Liabilities',
            short_text: 'Liabilities',
            account_group: 'LIAB',
            account_type: 'liability',
            reconciliation_acct_flag: false,
            allow_postings: false,
            blocked_flag: false,
            group_account_number: ''
          },
          {
            account_number: '3000',
            account_name: 'Total Equity',
            short_text: 'Equity',
            account_group: 'EQUITY',
            account_type: 'equity',
            reconciliation_acct_flag: false,
            allow_postings: false,
            blocked_flag: false,
            group_account_number: ''
          },
          {
            account_number: '4000',
            account_name: 'Total Revenue',
            short_text: 'Revenue',
            account_group: 'REV',
            account_type: 'revenue',
            reconciliation_acct_flag: false,
            allow_postings: false,
            blocked_flag: false,
            group_account_number: ''
          },
          {
            account_number: '5000',
            account_name: 'Total Expenses',
            short_text: 'Expenses',
            account_group: 'EXP',
            account_type: 'expense',
            reconciliation_acct_flag: false,
            allow_postings: false,
            blocked_flag: false,
            group_account_number: ''
          }
        ]
      };
    } else if (templateName === 'EMPTY') {
      // Empty template for custom creation
      const coaIdSuggestion = `NEW${Math.floor(Math.random() * 100).toString().padStart(2, '0')}`;
      
      template = {
        coa_id: coaIdSuggestion,
        name: '',
        description: '',
        coa_type: 'operational',
        currency: '',
        accounts: []
      };
    }
    
    setFormData(template);
  };

  const openGLAccountDialog = (account = null) => {
    if (account) {
      setEditingGLAccount(account);
      setGLAccountForm({ ...account });
    } else {
      setEditingGLAccount(null);
      setGLAccountForm({
        account_number: '',
        account_name: '',
        short_text: '',
        account_group: 'ASSET',
        account_type: 'asset',
        reconciliation_acct_flag: false,
        allow_postings: true,
        blocked_flag: false,
        group_account_number: ''
      });
    }
    setShowGLAccountDialog(true);
  };

  const handleGLAccountSubmit = () => {
    // Validate account number uniqueness
    const existingAccount = formData.accounts.find(
      acc => acc.account_number === glAccountForm.account_number && 
             (!editingGLAccount || acc.account_number !== editingGLAccount.account_number)
    );
    
    if (existingAccount) {
      toast.error(`Account number ${glAccountForm.account_number} already exists`);
      return;
    }

    if (editingGLAccount) {
      // Update existing account
      const updatedAccounts = formData.accounts.map(acc =>
        acc.account_number === editingGLAccount.account_number ? { ...glAccountForm } : acc
      );
      setFormData(prev => ({ ...prev, accounts: updatedAccounts }));
    } else {
      // Add new account
      setFormData(prev => ({ 
        ...prev, 
        accounts: [...prev.accounts, { ...glAccountForm }] 
      }));
    }

    setShowGLAccountDialog(false);
  };

  const removeGLAccount = (accountNumber) => {
    setFormData(prev => ({
      ...prev,
      accounts: prev.accounts.filter(acc => acc.account_number !== accountNumber)
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.coa_id || !formData.name) {
      toast.error('COA ID and Name are required');
      return;
    }
    
    if (formData.accounts.length === 0) {
      toast.error('At least one GL Account is required');
      return;
    }

    onSubmit(formData);
  };

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="space-y-6">
      {/* Template Selection */}
      {!editingItem && (
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
          <Label className="text-sm font-semibold mb-2 block">Quick Templates</Label>
          <div className="flex gap-2">
            <Button type="button" size="sm" variant="outline" onClick={() => applyTemplate('STANDARD')}>
              <Copy className="w-3 h-3 mr-1" /> Standard COA
            </Button>
            <Button type="button" size="sm" variant="outline" onClick={() => applyTemplate('GROUP')}>
              <Copy className="w-3 h-3 mr-1" /> Group COA
            </Button>
            <Button type="button" size="sm" variant="outline" onClick={() => applyTemplate('EMPTY')}>
              <Copy className="w-3 h-3 mr-1" /> Empty COA
            </Button>
          </div>
          <div className="text-xs text-gray-600 mt-2">
            <strong>Standard:</strong> Operational COA with detailed accounts (100000-500000)<br/>
            <strong>Group:</strong> Consolidation COA with summary accounts (1000-5000)<br/>
            <strong>Empty:</strong> Start from scratch with no pre-defined accounts
          </div>
        </div>
      )}

      {/* COA Basic Fields */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>COA ID *</Label>
          <Input
            value={formData.coa_id}
            onChange={(e) => updateField('coa_id', e.target.value.toUpperCase())}
            maxLength={4}
            required
            disabled={!!editingItem}
            placeholder="e.g., YCOA, INT"
          />
          <p className="text-xs text-gray-500 mt-1">Up to 4 characters, uppercase</p>
        </div>
        
        <div>
          <Label>Name *</Label>
          <Input
            value={formData.name}
            onChange={(e) => updateField('name', e.target.value)}
            required
            placeholder="e.g., Global Operational Chart"
          />
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div>
          <Label>COA Type *</Label>
          <Select value={formData.coa_type} onValueChange={(value) => updateField('coa_type', value)}>
            <SelectTrigger>
              <SelectValue placeholder="Select COA type" />
            </SelectTrigger>
            <SelectContent>
              {coaTypes.map(type => (
                <SelectItem key={type.value} value={type.value}>
                  {type.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label>Currency (Optional)</Label>
          <Input
            value={formData.currency}
            onChange={(e) => updateField('currency', e.target.value.toUpperCase())}
            maxLength={3}
            placeholder="e.g., USD, EUR"
          />
        </div>

        <div>
          <Label>Description</Label>
          <Input
            value={formData.description}
            onChange={(e) => updateField('description', e.target.value)}
            placeholder="Free text description"
          />
        </div>
      </div>

      {/* GL Accounts Management */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <Label className="text-lg font-semibold">
            GL Accounts ({formData.accounts.length})
          </Label>
          <Button type="button" size="sm" onClick={() => openGLAccountDialog()}>
            <Plus className="w-3 h-3 mr-1" /> Add Account
          </Button>
        </div>

        <div className="max-h-96 overflow-y-auto border rounded-lg">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 sticky top-0">
              <tr>
                <th className="px-2 py-2 text-left">Account #</th>
                <th className="px-2 py-2 text-left">Account Name</th>
                <th className="px-2 py-2 text-left">Type</th>
                <th className="px-2 py-2 text-left">Group</th>
                <th className="px-2 py-2 text-left">Flags</th>
                <th className="px-2 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {formData.accounts.map((account, index) => (
                <tr key={account.account_number} className="border-b hover:bg-gray-50">
                  <td className="px-2 py-2 font-mono font-semibold">{account.account_number}</td>
                  <td className="px-2 py-2">
                    <div>{account.account_name}</div>
                    {account.short_text && (
                      <div className="text-xs text-gray-500">{account.short_text}</div>
                    )}
                  </td>
                  <td className="px-2 py-2">
                    <span className="px-2 py-1 bg-orange-100 text-orange-700 text-xs rounded">
                      {account.account_type}
                    </span>
                  </td>
                  <td className="px-2 py-2 font-mono text-xs">{account.account_group}</td>
                  <td className="px-2 py-2">
                    <div className="flex gap-1">
                      {account.reconciliation_acct_flag && (
                        <span className="px-1 py-0.5 bg-orange-100 text-orange-700 text-xs rounded">Recon</span>
                      )}
                      {!account.allow_postings && (
                        <span className="px-1 py-0.5 bg-yellow-100 text-yellow-700 text-xs rounded">No Post</span>
                      )}
                      {account.blocked_flag && (
                        <span className="px-1 py-0.5 bg-red-100 text-red-700 text-xs rounded">Blocked</span>
                      )}
                    </div>
                  </td>
                  <td className="px-2 py-2">
                    <div className="flex gap-1">
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={() => openGLAccountDialog(account)}
                      >
                        <Edit className="w-3 h-3" />
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={() => removeGLAccount(account.account_number)}
                      >
                        <Trash2 className="w-3 h-3 text-red-500" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          
          {formData.accounts.length === 0 && (
            <div className="p-8 text-center text-gray-500">
              No GL accounts defined. Click "Add Account" to create your first account.
            </div>
          )}
        </div>
      </div>

      {/* Form Actions */}
      <div className="flex gap-3 pt-4 border-t">
        <Button type="button" onClick={handleSubmit} className="flex-1">
          {editingItem ? 'Update' : 'Create'} Chart of Accounts
        </Button>
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
      </div>

      {/* GL Account Dialog */}
      <Dialog open={showGLAccountDialog} onOpenChange={setShowGLAccountDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {editingGLAccount ? 'Edit' : 'Add'} GL Account
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Account Number *</Label>
                <Input
                  value={glAccountForm.account_number}
                  onChange={(e) => setGLAccountForm(prev => ({ ...prev, account_number: e.target.value.toUpperCase() }))}
                  maxLength={10}
                  disabled={!!editingGLAccount}
                  placeholder="e.g., 100000"
                />
              </div>
              
              <div>
                <Label>Account Group *</Label>
                <Select 
                  value={glAccountForm.account_group} 
                  onValueChange={(value) => setGLAccountForm(prev => ({ ...prev, account_group: value }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select group" />
                  </SelectTrigger>
                  <SelectContent>
                    {accountGroups.map(group => (
                      <SelectItem key={group} value={group}>{group}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>Account Name *</Label>
              <Input
                value={glAccountForm.account_name}
                onChange={(e) => setGLAccountForm(prev => ({ ...prev, account_name: e.target.value }))}
                placeholder="e.g., Cash and Cash Equivalents"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Short Text</Label>
                <Input
                  value={glAccountForm.short_text}
                  onChange={(e) => setGLAccountForm(prev => ({ ...prev, short_text: e.target.value }))}
                  maxLength={20}
                  placeholder="e.g., Cash"
                />
                <p className="text-xs text-gray-500">Max 20 characters</p>
              </div>

              <div>
                <Label>Account Type *</Label>
                <Select 
                  value={glAccountForm.account_type} 
                  onValueChange={(value) => setGLAccountForm(prev => ({ ...prev, account_type: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {accountTypes.map(type => (
                      <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>Group Account Number (For consolidation)</Label>
              <Input
                value={glAccountForm.group_account_number}
                onChange={(e) => setGLAccountForm(prev => ({ ...prev, group_account_number: e.target.value }))}
                maxLength={10}
                placeholder="Optional"
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="reconciliation_flag"
                  checked={glAccountForm.reconciliation_acct_flag}
                  onCheckedChange={(checked) => setGLAccountForm(prev => ({ ...prev, reconciliation_acct_flag: checked }))}
                />
                <Label htmlFor="reconciliation_flag" className="text-sm">
                  Reconciliation Account
                </Label>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="allow_postings"
                  checked={glAccountForm.allow_postings}
                  onCheckedChange={(checked) => setGLAccountForm(prev => ({ ...prev, allow_postings: checked }))}
                />
                <Label htmlFor="allow_postings" className="text-sm">
                  Allow Postings
                </Label>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="blocked_flag"
                  checked={glAccountForm.blocked_flag}
                  onCheckedChange={(checked) => setGLAccountForm(prev => ({ ...prev, blocked_flag: checked }))}
                />
                <Label htmlFor="blocked_flag" className="text-sm">
                  Blocked
                </Label>
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              <Button onClick={handleGLAccountSubmit} className="flex-1">
                {editingGLAccount ? 'Update' : 'Add'} Account
              </Button>
              <Button variant="outline" onClick={() => setShowGLAccountDialog(false)}>
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ChartOfAccountsForm;